import { useEffect, useRef, useState } from "react";
import { QueryClient, useMutation, useQueryClient } from "@tanstack/react-query";
import { IAppendingConfigurationItem, IAppendingConfigurationReference, ISystemSettings } from "@/Settings/types/Common/model";
import { Grid } from "@mui/material";
import Title from "../../../common/Title";
import { hasFranchises } from "@/Settings/Constants/AppendingConfig";
import {
	addObjectToConfiguration,
	clearAppendingConfig,
	extractGlAccounts,
	removeObjectFromIds,
	removeStartIfMatches,
	replaceObjectById,
	updateOriginalArray,
} from "@/Settings/helpers/SystemSettings/appendingConfiguration";
import AppendingConfigFranchisesSection from "./AppendingConfigFranchisesSection";
import { IAppendingConfigInfo, IFSAccount, IGlAccount, IOem, IRow, IWSGroup } from "@/Settings/types/SystemSettings/appendingConfiguration";
import AppendingConfigNoFranchisesSection from "./AppendingConfigNoFranchisesSection";
import { updateSystemSettings } from "@/Settings/api/SystemSettings/mutations";
import { errorLogger } from "@/Common/helpers/logger";
import { cloneDeep } from "lodash";

const AppendingConfiguration = (props: {
	locationId: string;
	position: string;
	isDepartmental?: boolean;
	appendingConfiguration?: IAppendingConfigurationItem[];
	oems: IOem[];
	openCollapse: boolean;
}) => {
	const { locationId, position, appendingConfiguration = [], isDepartmental, oems, openCollapse } = props;
	const queryClient = useQueryClient();
	const {
		getFSAccounts: fsAccounts,
		getGlAccounts: glAccounts,
		getWSGroups: wsGroups,
	} = queryClient.getQueryData(["appendingConfigInfo", locationId]) as IAppendingConfigInfo;
	const firstRender = useRef(false);
	const [internalAppending, setInternalAppending] = useState<IAppendingConfigurationItem[]>([]);

	const stringAppending = JSON.stringify(appendingConfiguration);
	useEffect(() => {
		if (firstRender.current) {
			setInternalAppending((prev) => updateOriginalArray(prev, appendingConfiguration));
		} else {
			setInternalAppending(appendingConfiguration);
			firstRender.current = true;
		}
	}, [stringAppending]);

	const isFinanceDirector = position === "Finance Director";

	if (isFinanceDirector && !isDepartmental && !openCollapse) {
		return <></>;
	}

	if (internalAppending.length) {
		const sortedAppendingData = [...internalAppending].sort((a, b) => a.position.localeCompare(b.position));
		return sortedAppendingData.map((item) => (
			<GeneralSection
				key={`${item._id}-${item?.position}`}
				globalPosition={position}
				position={item?.position}
				data={item.data}
				glAccounts={glAccounts}
				fsAccounts={fsAccounts}
				wsGroups={wsGroups}
				locationId={locationId}
				oems={oems}
				queryClient={queryClient}
			/>
		));
	}

	return null;
};
type TGeneralSectionProps = {
	locationId: string;
	position: string;
	globalPosition: string;
	glAccounts: IGlAccount[];
	fsAccounts: IFSAccount[];
	wsGroups: IWSGroup[];
	data: IAppendingConfigurationReference[];
	oems: IOem[];
	queryClient: QueryClient;
};

const GeneralSection = (props: TGeneralSectionProps) => {
	const { globalPosition, position, data = [], fsAccounts = [], glAccounts = [], wsGroups = [], locationId, oems, queryClient } = props;
	const title = `${removeStartIfMatches(position, globalPosition)} Departmental Appending Configurations`;
	const appendingConfigFranchises = data.filter((item) => hasFranchises.includes(item.reference));
	const noAppendingConfigFranchises = data.filter((item) => !hasFranchises.includes(item.reference));

	const resolveGlAccounts = () => {
		const alreadyUsedGlAccounts = extractGlAccounts(data);
		const allFsAccounts = fsAccounts?.map((entry) => {
			return {
				...entry,
				processes: entry?.processes?.flatMap((process) => process?.accounts ?? []),
			};
		});
		const allSelectableGlAccounts = glAccounts
			?.filter((accItem) => !!accItem?.glAccount?.glAccount && accItem?.glAccount?.accountType !== "COUNT_ACCOUNTS")
			.filter((item) => !alreadyUsedGlAccounts.includes(item.glAccount.glAccount))
			.map((accItem) => ({
				...accItem,
				label: accItem.glAccount?.glAccount,
			}));
		const filteredGlAccounts = allSelectableGlAccounts
			.reduce<IGlAccount[]>((acc, val) => {
				const foundFsAccount = allFsAccounts?.find(
					(item) => item?.oemReference === val?.oemReference && item?.dealerCodeFS === val?.dealerCodeFS
				);
				if (foundFsAccount?.processes?.includes(val?.fsAccount)) {
					return acc;
				}
				return [...acc, val];
			}, [])
			.filter((item) => !alreadyUsedGlAccounts.includes(item.glAccount.glAccount));
		return { allSelectableGlAccounts, filteredGlAccounts };
	};

	const { mutate, isPending } = useMutation({
		mutationFn: (input: { appendingConfiguration: { position: string; data: IAppendingConfigurationReference[] } }) => {
			return updateSystemSettings(locationId, input, "APPENDING_CONFIGURATION", oems);
		},
		onSuccess: (successData: ISystemSettings) => {
			queryClient.setQueryData(["systemSettings", locationId], (oldData: ISystemSettings) => {
				return {
					...oldData,
					appendingConfiguration: successData?.appendingConfiguration,
				};
			});
		},
		onError: (error) => errorLogger(error),
	});

	const handleSaveConfiguration = async ({
		finalRow,
		reference,
		franchise,
	}: {
		finalRow?: IRow;
		reference: string;
		franchise?: string | null;
	}) => {
		const { oemReference, dealerCodeFS, ...row } = finalRow ?? { _id: "" };
		const clonedAppendingConfigurationInfo = cloneDeep(data);
		let newAppendingConfigurationInfo;
		if (JSON.stringify(clonedAppendingConfigurationInfo).includes(row?._id)) {
			newAppendingConfigurationInfo = replaceObjectById({
				clonedAppendingConfigurationInfo,
				row,
			});
		} else {
			newAppendingConfigurationInfo = addObjectToConfiguration({
				clonedAppendingConfigurationInfo,
				reference,
				oemReference,
				dealerCodeFS,
				row,
				franchise,
			});
		}
		const cleanAppendingConfig = clearAppendingConfig(newAppendingConfigurationInfo) as IAppendingConfigurationReference[];
		const input = {
			appendingConfiguration: { position, data: cleanAppendingConfig },
		};

		return mutate(input);
	};

	const handleRemoveConfiguration = async (ids: string[]) => {
		const clonedAppendingConfigurationInfo = cloneDeep(data);
		const newAppendingConfigurationInfo = removeObjectFromIds(clonedAppendingConfigurationInfo, ids);
		const cleanAppendingConfig = clearAppendingConfig(newAppendingConfigurationInfo) as IAppendingConfigurationReference[];
		const input = {
			appendingConfiguration: { position, data: cleanAppendingConfig },
		};
		return mutate(input);
	};

	const handleMultipleSaveConfiguration = async (rows: IRow[]) => {
		let clonedAppendingConfigurationInfo = cloneDeep(data);
		for (const finalRow of rows) {
			const { oemReference, dealerCodeFS, ...row } = finalRow;
			clonedAppendingConfigurationInfo = replaceObjectById({ clonedAppendingConfigurationInfo, row });
		}
		const cleanAppendingConfig = clearAppendingConfig(clonedAppendingConfigurationInfo) as IAppendingConfigurationReference[];
		const input = {
			appendingConfiguration: { position, data: cleanAppendingConfig },
		};
		return mutate(input);
	};

	return (
		<Grid container direction="column" sx={{ width: "100%" }}>
			<Title title={title} className="!mb-[24px]" />
			<Grid item sx={{ width: "100%" }}>
				{appendingConfigFranchises?.map((item, index) => (
					<AppendingConfigFranchisesSection
						key={`appendingConfigFranchises-item-${item?._id || index}`}
						reference={item.reference}
						oems={item.oems}
						resolveGlAccounts={resolveGlAccounts}
						wsGroups={wsGroups}
						onSaveConfiguration={handleSaveConfiguration}
						onRemoveConfiguration={handleRemoveConfiguration}
						onMultipleSaveConfiguration={handleMultipleSaveConfiguration}
						isPending={isPending}
					/>
				))}
			</Grid>
			<Grid item sx={{ width: "100%" }}>
				<AppendingConfigNoFranchisesSection
					data={noAppendingConfigFranchises}
					resolveGlAccounts={resolveGlAccounts}
					wsGroups={wsGroups}
					onSaveConfiguration={handleSaveConfiguration}
					onRemoveConfiguration={handleRemoveConfiguration}
					onMultipleSaveConfiguration={handleMultipleSaveConfiguration}
					isPending={isPending}
				/>
			</Grid>
		</Grid>
	);
};

export default AppendingConfiguration;
